import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { adminLogin, setToken, adminMe, apiError } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";

export default function AdminLogin() {
  const { admin, setAdmin, logout } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({ email, password });
      setToken(res.token);
      const me = await adminMe();
      setAdmin(me);
      toast.success("Welcome back. Moderation tools are now active.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (admin) {
    return (
      <div data-testid="admin-loggedin" className="pt-40 pb-40 max-w-md mx-auto px-6 text-center">
        <p className="text-xs tracking-accent uppercase text-amber-500 mb-4">Moderator</p>
        <h1 className="font-display text-4xl font-black mb-4">You're logged in</h1>
        <p className="text-zinc-400 mb-8">Moderation controls (pin, hide, delete, reply as author) are active across the community.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate("/admin/dashboard")} className="bg-amber-500 text-zinc-950 font-bold text-sm px-6 py-3 hover:bg-amber-400 transition-colors">Go to dashboard</button>
          <button onClick={() => navigate("/community")} className="border border-zinc-700 text-sm px-6 py-3 hover:border-amber-500 hover:text-amber-500 transition-colors">Community</button>
          <button data-testid="admin-logout" onClick={logout} className="border border-zinc-700 text-sm px-6 py-3 hover:border-amber-500 hover:text-amber-500 transition-colors">Log out</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-login-page" className="pt-40 pb-40 max-w-md mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs tracking-accent uppercase text-amber-500 mb-4 inline-flex items-center gap-2"><Lock size={14} /> Author access</p>
        <h1 className="font-display text-4xl font-black tracking-tight mb-8">Moderator Login</h1>
        <form onSubmit={submit} className="space-y-5">
          <input data-testid="admin-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          <input data-testid="admin-password" required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-6 py-4 hover:bg-amber-400 transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
